import { RecurringTransactionFrequency, RecurringTransactionType } from '../../__generated__/globalTypes';
import { formatCurrencyWhole, formatMoneyShort, toCurrency } from '../formatting';

/**
 * Returns the number of times per year a recurring transaction occurs
 */
export function getYearlyMultiplier(frequency: RecurringTransactionFrequency): number {
  switch (frequency) {
    case RecurringTransactionFrequency.Weekly:
      return 52;
    case RecurringTransactionFrequency.Biweekly:
      return 26;
    case RecurringTransactionFrequency.SemiMonthly:
      return 24;
    case RecurringTransactionFrequency.Monthly:
      return 12;
    case RecurringTransactionFrequency.Annually:
      return 1;
    case RecurringTransactionFrequency.OneTime:
      return 0;
    case RecurringTransactionFrequency.Unknown:
    default:
      return 12; // Default to monthly if unknown
  }
}

/**
 * Returns a human-readable label for the frequency
 */
export function getFrequencyLabel(frequency: RecurringTransactionFrequency): string {
  switch (frequency) {
    case RecurringTransactionFrequency.Weekly:
      return 'Weekly';
    case RecurringTransactionFrequency.Biweekly:
      return 'Biweekly';
    case RecurringTransactionFrequency.SemiMonthly:
      return 'Semi-monthly';
    case RecurringTransactionFrequency.Monthly:
      return 'Monthly';
    case RecurringTransactionFrequency.Annually:
      return 'Annual';
    case RecurringTransactionFrequency.OneTime:
      return 'One-time';
    case RecurringTransactionFrequency.Unknown:
      return 'Fluctuating';
    default:
      return 'Fluctuating';
  }
}

/**
 * Returns a human-readable label for the recurring transaction type
 */
export function formatRecurringType(type: RecurringTransactionType): string {
  switch (type) {
    case RecurringTransactionType.Bill:
      return 'Bill';
    case RecurringTransactionType.Subscription:
      return 'Subscription';
    case RecurringTransactionType.Income:
      return 'Income';
    default:
      return 'Other';
  }
}

/**
 * Calculates the projected yearly spend for a recurring transaction
 */
export function calculateYearlySpend(amountRange: string[], frequency: RecurringTransactionFrequency): number {
  if (amountRange.length === 0) {
    return 0;
  }

  const averageAmount = amountRange.reduce((acc, curr) => acc + Number(curr), 0) / amountRange.length;
  const amount = Number(averageAmount);
  const multiplier = getYearlyMultiplier(frequency);
  return amount * multiplier;
}

/**
 * Formats an amount range for display.
 *
 * Single value  → full currency with cents: `$14.99`
 * Range (2 values) → cents dropped, joined with " - ":
 *   - `abbreviated: true`  (default) → `$1.85K - $2.15K`  (tables)
 *   - `abbreviated: false`           → `$1,850 - $2,145`
 */
export function getAverageAmountLabel(amountRange: string[], options?: { abbreviated?: boolean }): string {
  const { abbreviated = true } = options ?? {};

  if (amountRange.length === 0) {
    return '$0';
  }

  if (amountRange.length === 1) {
    return toCurrency(amountRange[0]);
  }

  const formatRange = abbreviated
    ? (amount: string) => formatMoneyShort(Math.round(Number(amount)))
    : (amount: string) => formatCurrencyWhole(Math.round(Number(amount)));

  return amountRange.map(formatRange).join(' - ');
}
